import $ from 'jquery';
import {formatStatus, formatCurrency, formatDisplayDate, uniqueListener, EMPTY_VALUE} from './helpers.js';

export class tables {
  static init(){
    const locale = (typeof pageData !== 'undefined' && pageData.locale) || document.documentElement.getAttribute('lang') || document.body.getAttribute('lang') || 'ja';

    if(typeof pageData !== 'undefined' && pageData.latestReleases){
      $('.table-latest').bootstrapTable({
        columns: latestReleasesColumns(),
        data: pageData.latestReleases,
        rowAttributes: releaseRowAttributes,
        sortName: 'created_at',
        sortOrder: 'desc',
        locale: locale,
      })
    }

    if(typeof pageData !== 'undefined' && pageData.allReleases){
      $('.table-all').bootstrapTable({
        columns: latestReleasesColumns(),
        data: pageData.allReleases,
        rowAttributes: releaseRowAttributes,
        pagination: true,
        // 25 keeps the whole list on one page for every account we have seen, so the pagination row
        // stays out of the way of a list that reads as a card. It is still there when it is needed.
        pageSize: 25,
        sortName: 'created_at',
        sortOrder: 'desc',
        locale: locale,
        formatNoMatches: noMatchesFormatter,
      })
      initReleaseStatusFilters();
    }

    if(typeof pageData !== 'undefined' && pageData.mediaLists){
      $('.table-media-lists').bootstrapTable({
        columns: mediaListsColumns(),
        data: pageData.mediaLists,
        pagination: false,
        locale: locale,
      })
    }
  };
};

// A draft can be saved before it has a title, and `${null}` renders the literal string "null".
// The placeholder is an i18n value carried on the same `pageData.columnTitles` channel the column
// headers use (see the <script> preamble in app/views/dashboard/show.html.erb).
const columnTitle = key => (typeof pageData !== 'undefined' && pageData.columnTitles ? pageData.columnTitles[key] : undefined);

const escapeHtml = text => $('<div>').text(text == null ? '' : text).html();

// Paid/Free pill next to the title (design-system ReleaseRow / TierBadge). `Release#tier` is derived
// from the price and is **null** for a release whose price was never calculated — a fresh draft —
// and those rows carry no pill at all. Labels ride the `pageData.columnTitles` channel the column
// headers and the "(untitled)" placeholder already use.
const tierPill = tier => {
  if(tier !== 'free' && tier !== 'paid') {
    return '';
  }
  const label = columnTitle(tier === 'free' ? 'tier_free' : 'tier_paid') || '';

  return `<span class="tier-pill tier-pill-${tier}">${escapeHtml(label)}</span>`;
};

const titleFormatter = (titleValue, row) => {
  const title = typeof titleValue === 'string' ? titleValue.trim() : '';
  const label = title || columnTitle('untitled') || EMPTY_VALUE;

  return `<div class='table-title-cell'><p class='table-item-title'> ${escapeHtml(label)} </p>${tierPill(row && row.tier)}</div>`;
};

// "Release rows carry a 3px status colour on the left edge" (docs/design-system/readme.md).
// The colour is drawn in CSS from `data-status` on the <tr>; see the `$release-status-edges` map in
// app/javascript/stylesheets/mack/sass/components/common/_table.scss. Adding an attribute only — no
// existing class, id or data attribute changes.
const releaseRowAttributes = row => (row && row.status ? { 'data-status': row.status } : {});

export const statusFormatter = (status, row) => {
  if(!status) {
    return '';
  }
  const statusFormatted = formatStatus(row?.status_name || status);
  const chipClass =
    (status == 'draft' || status == 'waiting_for_payment') ? 'draft' :
    (status == 'submitted') ? 'submitted' :
    (status == 'content_ready') ? 'contentReady' :
    (status == 'pending_customer_confirmation') ? 'pendingConfirmation' :
    (status == 'ready_to_publish') ? 'readyToPublish' :
    (status == 'published') ? 'published' :
    (status == 'report_ready') ? 'reportReady' :
    (status == 'completed') ? 'completed' :
    (status == 'paid') ? 'success' :
    (status == 'submitted') ? 'danger' :
    'default';
    return `<div class='chip size-medium variant-${chipClass}'><p>${statusFormatted}</p></div>`;
};

// Table dates read as a date only — "Jul 28, 2026" / "2026年8月5日". The exact time still matters on
// the release detail page, not in a list row.
const dateFormatter = value => formatDisplayDate(value, typeof pageData !== 'undefined' ? pageData.user?.timezone : undefined);

// Price is the one right-aligned column in the row (ReleasesScreen.jsx: `textAlign: 'right'`), and
// the only figure in it carries the ReleaseRow's 13px / 600 / --ink-1.
const currencyFormatter = value => {
  const amount = formatCurrency(parseFloat(value));

  return amount ? `<span class="table-item-price">${amount}</span>` : '';
};

// Row actions — design-system ReleaseRow: 30px radius-8 icon buttons on the right edge of the row,
// no text links. `Release#actions` still renders "Show | Edit | Delete" as anchors, and it has to:
// it owns the routes, the locale, the `draft?` conditional and the Rails UJS delete attributes
// (`data-method`, `data-confirm`). Rewriting the payload would move all four into JS, so the
// anchors are re-dressed here instead and every attribute they arrived with is kept. The label
// stays in the button as visually-hidden text — it is what a screen reader and the e2e specs read.
const ACTION_ICONS = {
  show:   { icon: 'mdi-eye-outline',        tone: 'muted'   },
  edit:   { icon: 'mdi-pencil-outline',     tone: 'success' },
  delete: { icon: 'mdi-trash-can-outline',  tone: 'danger'  },
};

const actionKind = anchor => {
  if((anchor.getAttribute('data-method') || '').toLowerCase() === 'delete') return 'delete';
  if(/\/edit(?:[/?#]|$)/.test(anchor.getAttribute('href') || '')) return 'edit';
  return 'show';
};

const actionsFormatter = value => {
  if(!value) {
    return '';
  }

  const source = document.createElement('div');
  source.innerHTML = value;

  const anchors = Array.from(source.querySelectorAll('a'));
  if(!anchors.length) {
    return value;
  }

  const cell = document.createElement('div');
  cell.className = 'table-actions';

  anchors.forEach(anchor => {
    const kind = actionKind(anchor);
    const { icon, tone } = ACTION_ICONS[kind];
    const label = anchor.textContent.trim();

    anchor.classList.add('btn', 'btn-icon', 'btn-icon-small', `btn-icon-${tone}`, `table-action-${kind}`);
    anchor.setAttribute('title', label);

    const glyph = document.createElement('span');
    glyph.className = `mdi ${icon}`;
    glyph.setAttribute('aria-hidden', 'true');

    // textContent, never innerHTML: the label is an i18n value and stays text.
    const srLabel = document.createElement('span');
    srLabel.className = 'sr-only';
    srLabel.textContent = label;

    anchor.textContent = '';
    anchor.append(glyph, srLabel);
    cell.append(anchor);
  });

  return cell.outerHTML;
};

// Status filters over the releases list (ReleasesScreen.jsx `SegmentedControl`). The grouping is
// the product's own lifecycle (.claude/rules/domain.md), split into the same four named buckets the
// dashboard stat tiles count (`DashboardController::STAT_BUCKETS`) so the two adjacent screens
// agree: what sits with the GPRW editors is "in review", what is waiting on the customer or on the
// schedule is "in progress", and everything from publication on is "published". Filtering is
// client-side — bootstrap-table already holds the whole list.
const RELEASE_STATUS_FILTERS = {
  draft: ['draft'],
  in_progress: ['waiting_for_payment', 'pending_customer_confirmation', 'ready_to_publish'],
  in_review: ['submitted', 'content_ready'],
  published: ['published', 'report_ready', 'completed'],
};

const ACTIVE_FILTER_CLASS = 'segmented-option-active';

const applyReleaseStatusFilter = key => {
  const table = $('.table-all');
  if(!table.length) return;

  const statuses = RELEASE_STATUS_FILTERS[key];
  table.bootstrapTable('filterBy', statuses ? { status: statuses } : {});

  document.querySelectorAll('.segmented [data-release-filter]').forEach(option => {
    const active = option.dataset.releaseFilter === key;
    option.classList.toggle(ACTIVE_FILTER_CLASS, active);
    option.setAttribute('aria-pressed', active ? 'true' : 'false');
  });
};

// Delegated: the empty state's "clear filters" button carries `data-release-filter` too, and it is
// rebuilt by bootstrap-table every time the filter changes.
const handleReleaseFilterClick = event => {
  const option = event.target.closest('[data-release-filter]');
  if(!option) return;

  event.preventDefault();
  applyReleaseStatusFilter(option.dataset.releaseFilter);
};

const initReleaseStatusFilters = () => {
  if(!document.querySelector('.segmented [data-release-filter]')) return;

  // Turbolinks swaps <body>, so the chips are fresh nodes on every load; the listener is keyed on a
  // stable reference on `document` instead. A cached page comes back with whatever chip was
  // active, so the filter is re-applied from the DOM rather than assumed to be "all".
  uniqueListener(document, 'click', handleReleaseFilterClick);

  const active = document.querySelector(`.segmented [data-release-filter].${ACTIVE_FILTER_CLASS}`);
  applyReleaseStatusFilter(active ? active.dataset.releaseFilter : 'all');
};

// bootstrap-table's "no matching records" row, rendered as the design-system EmptyState with the
// way out the pattern asks for (docs/design-system/components/feedback/EmptyState.prompt.md).
const noMatchesFormatter = () => {
  const copy = (typeof pageData !== 'undefined' && pageData.noMatches) || {};
  const escape = text => $('<div>').text(text || '').html();

  return `<div class="empty-state table-empty-state">
    <div class="empty-state-icon"><span class="mdi mdi-filter-remove-outline" aria-hidden="true"></span></div>
    <p class="empty-state-title">${escape(copy.title)}</p>
    <p class="empty-state-body">${escape(copy.body)}</p>
    <div class="empty-state-action">
      <button type="button" class="btn btn-secondary btn-sm" data-release-filter="all">${escape(copy.button)}</button>
    </div>
  </div>`;
};

const latestReleasesColumns = () => [
  {
    field: 'title',
    title: typeof pageData !== 'undefined' && pageData.columnTitles ? pageData.columnTitles.title : '',
    // No fixed width: the title column takes what the row can spare and ellipsises at its own
    // ceiling (`.table-item-title` in components/common/_table).
    sortable: true,
    formatter: titleFormatter,
  },
  {
    field: 'created_at',
    title: typeof pageData !== 'undefined' && pageData.columnTitles ? pageData.columnTitles.created_at : '',
    sortable: true, 
    formatter: dateFormatter,
  },
  {
    field: 'publish_at',
    title: typeof pageData !== 'undefined' && pageData.columnTitles ? pageData.columnTitles.publish_at : '',
    sortable: true, 
    formatter: dateFormatter,
  },
  {
    field: 'status',
    title: typeof pageData !== 'undefined' && pageData.columnTitles ? pageData.columnTitles.status : '',
    sortable: true,
    formatter: statusFormatter,
  },
  {
    field: 'total_dollar_price',
    title: typeof pageData !== 'undefined' && pageData.columnTitles ? pageData.columnTitles.total_dollar_price : '',
    sortable: true,
    align: 'right',
    formatter: currencyFormatter,
  },
  {
    // The actions column has no header label: three icon buttons need no naming, and an "ACTIONS"
    // eyebrow over them is the loudest thing in a header row that is otherwise all column names
    // (ReleasesScreen.jsx ends its header row with an empty `<div />`). The field name stays —
    // bootstrap-table keys the cell on it, and `tables.column_titles.actions` still names the
    // column for anything that needs a word for it.
    field: 'actions',
    title: '',
    sortable: false,
    align: 'right',
    formatter: actionsFormatter,
  },
];

const mediaListsColumns = () => [
  {
    field: 'region_name',
    title: typeof pageData !== 'undefined' && pageData.columnTitles ? pageData.columnTitles.region_name : '',
    width: 150,
    sortable: true,
  },
  {
    field: 'name',
    title: typeof pageData !== 'undefined' && pageData.columnTitles ? pageData.columnTitles.name : '',
    width: 150,
    sortable: true,
  },
  {
    field: 'description',
    title: typeof pageData !== 'undefined' && pageData.columnTitles ? pageData.columnTitles.description : '',
  },
  {
    field: 'attachment_url',
    title: typeof pageData !== 'undefined' && pageData.columnTitles ? pageData.columnTitles.attachment_url : '',
    width: 150,
    align: 'center',
    formatter: url => `<a href="${url}" target="_blank" class="btn btn-outline-primary btn-sm btn-block">
      <span class="mdi mdi-download"></span>
      <span class="sr-only">${typeof pageData !== 'undefined' && pageData.columnTitles ? pageData.columnTitles.attachment_url : 'Download'}</span>
    </a>`,
  },
];
